import { z } from 'zod';
import {
  EXERCISE_KINDS,
  GAME_IDS,
  LEVEL_IDS,
  LOCALES,
  MISTAKE_TYPES,
  SCHOOL_TYPES,
  type MasteryStatus,
  type MistakeType,
} from './ids';

/**
 * Persisted learner records. These zod schemas are the single source of truth
 * for the local database, export files and import validation.
 *
 * Field classes (see docs/DATA-MODEL.md):
 * - required / optional as declared below;
 * - "derived" data (mastery) is never exported and can be rebuilt from attempts;
 * - "sensitive": `nickname` (chosen by the child, optional) is the only free-text
 *   field that could contain personal information.
 */

const isoDateTime = z.iso.datetime({ offset: true });
const id = z.uuid();

export const avatarSchema = z.object({
  character: z.string().min(1).max(16),
  color: z.string().regex(/^[a-z]+$/),
});

export const learnerPreferencesSchema = z.object({
  locale: z.enum(LOCALES),
  soundEffects: z.boolean(),
  speech: z.boolean(),
  largeText: z.boolean(),
  reducedMotion: z.boolean(),
  /** Answer some questions by speaking; switched on by a parent (FEATURES.voiceInput). */
  voiceAnswers: z.boolean().optional(),
});

export const learnerProfileSchema = z.object({
  id,
  /** Optional, chosen by the child; never required and never sent anywhere. */
  nickname: z.string().trim().max(24).optional(),
  avatar: avatarSchema,
  levelId: z.enum(LEVEL_IDS),
  schoolType: z.enum(SCHOOL_TYPES),
  variantId: z.string().min(1),
  curriculumVersionId: z.string().min(1),
  preferences: learnerPreferencesSchema,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type LearnerProfile = z.infer<typeof learnerProfileSchema>;
export type LearnerPreferences = z.infer<typeof learnerPreferencesSchema>;
export type Avatar = z.infer<typeof avatarSchema>;

export const SESSION_STATUSES = ['inProgress', 'completed', 'abandoned'] as const;

export const learningSessionSchema = z.object({
  id,
  learnerId: id,
  gameId: z.enum(GAME_IDS),
  subjectId: z.string().min(1).optional(),
  objectiveIds: z.array(z.string().min(1)).min(1),
  /** Seed of the exercise generator, so the session can be reproduced. */
  seed: z.number().int().nonnegative(),
  questionCount: z.number().int().nonnegative(),
  status: z.enum(SESSION_STATUSES),
  startedAt: isoDateTime,
  endedAt: isoDateTime.optional(),
  /** Filled when completed. */
  score: z.number().int().nonnegative().optional(),
  stars: z.number().int().min(0).max(3).optional(),
  isReview: z.boolean().optional(),
});
export type LearningSession = z.infer<typeof learningSessionSchema>;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const exerciseAttemptSchema = z.object({
  id,
  learnerId: id,
  sessionId: id,
  objectiveId: z.string().min(1),
  /** Stable key of the question (e.g. "add:3:4" or a content item id). */
  exerciseKey: z.string().min(1).max(200),
  kind: z.enum(EXERCISE_KINDS),
  /** 1 for the first try at a question, 2 for the retry, … */
  attemptNumber: z.number().int().min(1).max(10),
  correct: z.boolean(),
  hintUsed: z.boolean(),
  /** Time spent on this try, capped when stored. */
  responseMs: z.number().int().nonnegative(),
  mistakeType: z.enum(MISTAKE_TYPES).optional(),
  /** Short machine-readable trace of the answer, never free text from the child. */
  answer: z.string().max(200).optional(),
  /** Weight of this evidence for mastery (e.g. memory games count less). */
  evidenceWeight: z.number().min(0).max(1),
  timedOut: z.boolean().optional(),
  answeredAt: isoDateTime,
});
export type ExerciseAttempt = z.infer<typeof exerciseAttemptSchema>;

export const learnerAchievementSchema = z.object({
  learnerId: id,
  achievementId: z.string().min(1),
  unlockedAt: isoDateTime,
});
export type LearnerAchievement = z.infer<typeof learnerAchievementSchema>;

/** Derived cache, rebuilt from attempts: never exported, safe to delete. */
export interface MasteryRecord {
  learnerId: string;
  objectiveId: string;
  status: MasteryStatus;
  /** Weighted first-try score over the recent window, 0..1. */
  recentScore: number;
  attempts: number;
  firstTryCorrect: number;
  incorrect: number;
  hintsUsed: number;
  retriesCorrect: number;
  sessions: number;
  distinctDays: number;
  trend: 'improving' | 'stable' | 'declining' | 'unknown';
  topMistake?: MistakeType;
  reason: { code: string; params: Record<string, number | string> };
  /** Spaced repetition (Leitner box 0..5). */
  box: number;
  lastPracticedAt?: string;
  nextReviewAt?: string;
  updatedAt: string;
}

export const FEEDBACK_KINDS = ['rating', 'problem'] as const;
export const PROBLEM_REASONS = ['unclear', 'wrongAnswer', 'tooHard', 'something'] as const;

/**
 * Feedback given by a child inside a game. Children only tap a rating or a
 * reason (no free text); nothing leaves the device until a parent reviews and
 * sends it from the parent area.
 */
export const childFeedbackSchema = z.object({
  id,
  learnerId: id,
  kind: z.enum(FEEDBACK_KINDS),
  /** 1 = did not like, 2 = so-so, 3 = liked (ratings only). */
  rating: z.number().int().min(1).max(3).optional(),
  reason: z.enum(PROBLEM_REASONS).optional(),
  context: z.object({
    gameId: z.enum(GAME_IDS).optional(),
    objectiveId: z.string().max(120).optional(),
    exerciseKey: z.string().max(200).optional(),
  }),
  createdAt: isoDateTime,
  sentAt: isoDateTime.optional(),
});
export type ChildFeedback = z.infer<typeof childFeedbackSchema>;
